import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/lib/supabase';

interface Booking {
  id: number;
  firstname: string;
  lastname: string;
  email: string;
  phone: string;
  service: string;
  package: string;
  booking_date: string;
  booking_time: string;
  message: string;
  status: 'new' | 'confirmed' | 'done';
  created_at: string;
}

const SERVICE_KEYS: Record<string, string> = {
  hair: 'admin.service_hair',
  dental: 'admin.service_dental',
  surgery: 'adm1.service_surgery',
  botox: 'admin.service_botox',
  combined: 'admin.service_combined',
  consultation: 'admin.service_consultation',
};

const STATUS_CLASSES: Record<string, string> = {
  new: 'bg-accent-100 text-accent-800',
  confirmed: 'bg-green-100 text-green-700',
  done: 'bg-foreground-100 text-foreground-500',
};

const STATUS_KEYS: Record<string, string> = {
  new: 'admin.status_new',
  confirmed: 'admin.status_confirmed',
  done: 'admin.status_done',
};

export default function BookingsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const serviceLabel = (s: string) => (SERVICE_KEYS[s] ? t(SERVICE_KEYS[s]) : s);
  const statusLabel = (s: string) => (STATUS_KEYS[s] ? t(STATUS_KEYS[s]) : s);
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [savedMsg, setSavedMsg] = useState('');

  useEffect(() => {
    if (sessionStorage.getItem('admin_logged_in') !== 'true') {
      navigate('/admin');
      return;
    }
    loadBookings();
  }, [navigate]);

  const loadBookings = async () => {
    setLoading(true);
    setError('');
    try {
      const { data, error: supaError } = await supabase
        .from('bookings')
        .select('*')
        .order('booking_date', { ascending: true })
        .order('booking_time', { ascending: true });

      if (supaError) throw supaError;
      setBookings(data || []);
    } catch (err) {
      setError(t('adm1.bookings_load_error'));
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id: number, status: 'new' | 'confirmed' | 'done') => {
    const { error: supaError } = await supabase
      .from('bookings')
      .update({ status })
      .eq('id', id);

    if (supaError) {
      setError(t('adm1.bookings_status_error'));
      return;
    }

    setBookings((prev) => prev.map((b) => (b.id === id ? { ...b, status } : b)));
    setSavedMsg(t('adm1.bookings_saved_notified'));
    setTimeout(() => setSavedMsg(''), 2500);
    logActivity(`Buchung ${id} auf "${statusLabel(status)}" gesetzt`);

    // Email the customer about the status change (best-effort)
    const booking = bookings.find((b) => b.id === id);
    if (booking?.email) {
      try {
        const edgeUrl = `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/send-booking-confirmation`;
        await fetch(edgeUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({
            kind: 'status',
            status,
            email: booking.email,
            firstname: booking.firstname,
            lastname: booking.lastname,
            booking_date: booking.booking_date,
            booking_time: booking.booking_time,
          }),
        });
      } catch { /* email failure must not block the status update */ }
    }
  };

  const logActivity = (detail: string) => {
    const logKey = 'esteva_admin_activity_log';
    const existing = localStorage.getItem(logKey);
    const log: { action: string; timestamp: string; detail: string }[] = existing ? JSON.parse(existing) : [];
    log.unshift({ action: 'bookings_updated', timestamp: new Date().toISOString(), detail });
    localStorage.setItem(logKey, JSON.stringify(log.slice(0, 50)));
    window.dispatchEvent(new Event('admin-data-updated'));
  };

  const filtered = bookings
    .filter((b) => statusFilter === 'all' || b.status === statusFilter)
    .sort((a, b) => a.booking_date.localeCompare(b.booking_date) || a.booking_time.localeCompare(b.booking_time));

  const newCount = bookings.filter((b) => b.status === 'new').length;
  const confirmedCount = bookings.filter((b) => b.status === 'confirmed').length;
  const doneCount = bookings.filter((b) => b.status === 'done').length;

  const formatDate = (dateStr: string) => {
    const [y, m, d] = dateStr.split('-');
    const date = new Date(parseInt(y), parseInt(m) - 1, parseInt(d));
    const days = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];
    const months = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];
    return `${days[date.getDay()]}, ${parseInt(d)}. ${months[parseInt(m) - 1]} ${y}`;
  };

  if (sessionStorage.getItem('admin_logged_in') !== 'true') return null;

  return (
    <div className="px-4 py-6 md:px-6 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="font-heading text-xl font-bold text-foreground-900">{t('adm1.bookings_title')}</h1>
          <p className="mt-0.5 text-xs text-foreground-500 font-body break-words">
            {t('adm1.bookings_summary', { total: bookings.length, new: newCount, confirmed: confirmedCount, done: doneCount })}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {savedMsg && <span className="text-xs text-green-600 font-label">{savedMsg}</span>}
          <button
            onClick={loadBookings}
            className="flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-label bg-background-50 border border-background-200/70 text-foreground-500 hover:text-foreground-700 transition-all cursor-pointer whitespace-nowrap"
          >
            <i className="ri-refresh-line text-sm"></i>
            {t('adm1.refresh')}
          </button>
        </div>
      </div>

      {error && (
        <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-xs text-red-700 font-body">
          {error}
          <button onClick={loadBookings} className="ml-2 underline cursor-pointer">{t('adm1.retry')}</button>
        </div>
      )}

      {/* ── Status Filter ───────────────────────────── */}
      <div className="flex items-center gap-2 flex-wrap">
        {[
          { value: 'all', label: t('adm1.filter_all'), count: bookings.length },
          { value: 'new', label: t('admin.status_new'), count: newCount },
          { value: 'confirmed', label: t('admin.status_confirmed'), count: confirmedCount },
          { value: 'done', label: t('admin.status_done'), count: doneCount },
        ].map((f) => (
          <button
            key={f.value}
            onClick={() => setStatusFilter(f.value)}
            className={`px-3 py-2 rounded-full text-xs font-label transition-all cursor-pointer whitespace-nowrap ${
              statusFilter === f.value
                ? 'bg-foreground-900 text-white'
                : 'bg-background-50 border border-background-200/70 text-foreground-500 hover:text-foreground-700'
            }`}
          >
            {f.label} <span className="text-[10px] opacity-60">({f.count})</span>
          </button>
        ))}
      </div>

      {/* ── Bookings List ───────────────────────────── */}
      <div className="space-y-2">
        {loading && (
          <div className="text-center py-16">
            <i className="ri-loader-4-line animate-spin text-3xl text-foreground-300"></i>
            <p className="mt-3 text-sm text-foreground-400 font-body">{t('adm1.bookings_loading')}</p>
          </div>
        )}

        {!loading && filtered.map((booking) => (
          <div key={booking.id} className="bg-background-50 rounded-xl border border-background-200/70 overflow-hidden">
            <div className="p-4 flex items-center gap-3 sm:gap-4 cursor-pointer" onClick={() => setExpandedId(expandedId === booking.id ? null : booking.id)}>
              <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-accent-100 text-accent-700 flex-shrink-0">
                <span className="text-sm font-label font-bold">{booking.booking_time}</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-label font-semibold text-foreground-900 truncate">{booking.firstname} {booking.lastname}</p>
                <p className="text-[11px] text-foreground-500 font-body truncate">{formatDate(booking.booking_date)} · {serviceLabel(booking.service)}</p>
              </div>
              <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-label whitespace-nowrap ${STATUS_CLASSES[booking.status]}`}>
                {statusLabel(booking.status)}
              </span>
              <div className="w-5 h-5 flex items-center justify-center text-foreground-400">
                <i className={`ri-${expandedId === booking.id ? 'arrow-up-s' : 'arrow-down-s'}-line text-sm`}></i>
              </div>
            </div>

            {expandedId === booking.id && (
              <div className="px-5 pb-4 border-t border-background-100 pt-4">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                  <div>
                    <p className="text-[10px] font-label uppercase tracking-[0.1em] text-foreground-400">{t('adm1.label_email')}</p>
                    <p className="text-xs font-body text-foreground-800 break-words">{booking.email}</p>
                  </div>
                  <div>
                    <p className="text-[10px] font-label uppercase tracking-[0.1em] text-foreground-400">{t('adm1.label_phone')}</p>
                    <p className="text-xs font-body text-foreground-800 break-words">{booking.phone || '—'}</p>
                  </div>
                  <div>
                    <p className="text-[10px] font-label uppercase tracking-[0.1em] text-foreground-400">{t('adm1.label_package')}</p>
                    <p className="text-xs font-body text-foreground-800 capitalize">{booking.package || '—'}</p>
                  </div>
                  <div>
                    <p className="text-[10px] font-label uppercase tracking-[0.1em] text-foreground-400">{t('adm1.label_treatment')}</p>
                    <p className="text-xs font-body text-foreground-800">{serviceLabel(booking.service)}</p>
                  </div>
                </div>
                {booking.message && (
                  <div className="bg-background-100 rounded-lg p-3 mb-4">
                    <p className="text-xs font-body text-foreground-700 leading-relaxed">{booking.message}</p>
                  </div>
                )}
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[10px] font-label text-foreground-400 mr-2">{t('adm1.label_status')}</span>
                  {(['new', 'confirmed', 'done'] as const).map((s) => (
                    <button
                      key={s}
                      onClick={(e) => { e.stopPropagation(); updateStatus(booking.id, s); }}
                      className={`px-3 py-2 rounded-full text-[10px] font-label transition-all cursor-pointer whitespace-nowrap ${
                        booking.status === s
                          ? s === 'new' ? 'bg-accent-500 text-white' :
                            s === 'confirmed' ? 'bg-green-500 text-white' :
                            'bg-foreground-500 text-white'
                          : 'bg-background-100 text-foreground-400 hover:text-foreground-600'
                      }`}
                    >
                      {statusLabel(s)}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}

        {!loading && filtered.length === 0 && (
          <div className="text-center py-16">
            <i className="ri-calendar-check-line text-3xl text-foreground-300"></i>
            <p className="mt-3 text-sm text-foreground-400 font-body">{t('adm1.bookings_empty_title')}</p>
            <p className="mt-1 text-xs text-foreground-400 font-body">{t('adm1.bookings_empty_desc')}</p>
          </div>
        )}
      </div>
    </div>
  );
}